// frontend/src/App.jsx
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { Header } from './components/Layout/Header';
import { Footer } from './components/Layout/Footer';
import { HomePage } from './pages/HomePage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { LoginPage } from './pages/LoginPage';
import { ProfilePage } from './pages/ProfilePage';
import InstallationRequestPage from './pages/InstallationRequestPage';
import { MarketplacePage } from './pages/MarketplacePage';
import { CartPage } from './pages/CartPage';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { RegisterPage } from './pages/RegisterPage';
import { EditProductPage } from './pages/EditProductPage';
import { InstallersPage } from './pages/InstallersPage';
import { InstallerProfilePage } from './pages/InstallerProfilePage';
import { SellerProductsPage } from './pages/SellerProductsPage';
import { OrdersPage } from './pages/OrdersPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import { InstallationRequestsPage } from './pages/InstallationRequestsPage';
import { RequestDetailPage } from './pages/RequestDetailPage';
import { InstallerDashboard } from './pages/InstallerDashboard';
import { InstallerBidsPage } from './pages/InstallerBidsPage';
import { InstallerContractsPage } from './pages/InstallerContractsPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { InstallerRegisterForm } from './pages/InstallerRegisterForm';
import { InstallerSchedulePage } from './pages/InstallerSchedulePage';
import { ProjectProgressPage } from './pages/ProjectProgressPage';
import { InstallerEarningsPage } from './pages/InstallerEarningsPage';
import { OrderTracker } from './pages/OrderTracker';
import { ContractPage } from './pages/ContractPage';
import { ProjectTrackingPage } from './pages/ProjectTrackingPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { BlogPage } from './pages/BlogPage';
import { BlogPostPage } from './pages/BlogPostPage';
import { ContactPage } from './pages/ContactPage';
import { AboutPage } from './pages/AboutPage';
import { SellerOrdersPage } from './pages/SellerOrdersPage';
import { SellerDashboard } from './pages/SellerDashboard';
import { PackagesPage } from './pages/PackagesPage';
import { PackageDetailPage } from './pages/PackageDetailPage';
import { CategoryPage } from './pages/CategoryPage';
import TestRequestPage from './pages/TestRequestPage';
import { TestContract } from './pages/TestContract';
import { SellerPackagesPage } from './pages/SellerPackagesPage';
import { InstallerOrdersPage } from './pages/InstallerOrdersPage';
import NotificationPage from './pages/NotificationsPage';

// ========== مسیرهای ادمین ==========
import { AdminLayout } from './pages/admin/AdminLayout';
import { DashboardPage } from './pages/admin/DashboardPage';
import { AdminProductsPage } from './pages/admin/AdminProductsPage';
import { CategoriesListPage } from './pages/admin/CategoriesListPage';
import { EditCategoryPage } from './pages/admin/EditCategoryPage';
import { BlogListPage } from './pages/admin/BlogListPage';
import { EditBlogPostPage } from './pages/admin//EditBlogPostPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminOrdersPage } from './pages/admin/AdminOrdersPage';
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage';
import { AdminPackagesPage } from './pages/admin/AdminPackagesPage';
import { PackageEditPage } from './pages/admin/PackageEditPage';
import { AdminInstallationRequestsPage } from './pages/admin/AdminInstallationRequestsPage';
import { AdminMarketplacePage } from './pages/admin/AdminMarketplacePage';
import { AdminPackagePowerRatingsPage } from './pages/admin/AdminPackagePowerRatingsPage';

// ========== سئو ==========
import { SEOProvider } from './context/SEOContext';
import { GlobalSEO } from './components/SEO/GlobalSEO';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <SEOProvider>
            <GlobalSEO />
            <div className="min-h-screen bg-gray-50 flex flex-col font-sans text-gray-900">
              <Header />
              <main className="flex-grow">
                <Routes>
                  {/* ========== مسیرهای عمومی ========== */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/products/:slug" element={<ProductDetailPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/marketplace" element={<MarketplacePage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/installers" element={<InstallersPage />} />
                  <Route path="/installers/:id" element={<InstallerProfilePage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/category/:slug" element={<CategoryPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/orders" element={<OrdersPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/test-request" element={<TestRequestPage />} />
                  <Route path="/installation/request" element={<InstallationRequestPage />} />
                  <Route path="/test-contract/:contractId" element={<TestContract />} />
                  <Route path="/packages" element={<PackagesPage />} />
                  <Route path="/packages/:slug" element={<PackageDetailPage />} />
                  <Route path="/notifications" element={<NotificationPage />} />

                  {/* ========== مسیرهای فروشنده ========== */}
                  <Route path="/seller/dashboard" element={
                    <ProtectedRoute allowedRoles={['seller', 'admin']}>
                      <SellerDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/seller/products" element={
                    <ProtectedRoute allowedRoles={['seller', 'admin']}>
                      <SellerProductsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/products/:slug/edit" element={
                    <ProtectedRoute allowedRoles={['seller', 'admin']}>
                      <EditProductPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/seller/orders" element={
                    <ProtectedRoute allowedRoles={['seller', 'admin']}>
                      <SellerOrdersPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/seller/packages" element={
                    <ProtectedRoute allowedRoles={['seller', 'admin']}>
                      <SellerPackagesPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/seller/packages/new" element={
                    <ProtectedRoute allowedRoles={['seller', 'admin']}>
                      <PackageEditPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/seller/packages/:id/edit" element={
                    <ProtectedRoute allowedRoles={['seller', 'admin']}>
                      <PackageEditPage />
                    </ProtectedRoute>
                  } />

                  {/* ========== مسیرهای ادمین (با قالب AdminLayout) ========== */}
                  <Route path="/admin" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                      <AdminLayout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<DashboardPage />} />
                    <Route path="dashboard" element={<DashboardPage />} />
                    <Route path="products" element={<AdminProductsPage />} />
                    <Route path="packages" element={<AdminPackagesPage />} />
                    <Route path="packages/new" element={<PackageEditPage />} />
                    <Route path="packages/:id/edit" element={<PackageEditPage />} />
                    <Route path="categories" element={<CategoriesListPage />} />
                    <Route path="categories/:id/edit" element={<EditCategoryPage />} />
                    <Route path="categories/new" element={<EditCategoryPage />} />
                    <Route path="blog" element={<BlogListPage />} />
                    <Route path="blog/:slug/edit" element={<EditBlogPostPage />} />
                    <Route path="blog/new" element={<EditBlogPostPage />} />
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="orders" element={<AdminOrdersPage />} />
                    <Route path="settings" element={<AdminSettingsPage />} />
                    <Route path="installations" element={<AdminInstallationRequestsPage />} />
                    <Route path="marketplace" element={<AdminMarketplacePage />} />
                    <Route path="/admin/power-ratings" element={<AdminPackagePowerRatingsPage />} />
                  </Route>

                  {/* ========== مسیرهای عمومی محافظت شده ========== */}
                  <Route
                    path="/orders/:id"
                    element={
                      <ProtectedRoute>
                        <OrderDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/order/track/:orderId" element={<OrderTracker />} />
                  <Route path="/contract/:contractId" element={<ContractPage />} />
                  <Route path="/project/track/:contractId" element={<ProjectTrackingPage />} />

                  {/* ========== مسیرهای نصاب ========== */}
                  <Route path="/installer/register" element={<InstallerRegisterForm />} />
                  <Route path="/installer/dashboard" element={
                    <ProtectedRoute allowedRoles={['installer', 'admin']}>
                      <InstallerDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/installer/bids" element={
                    <ProtectedRoute allowedRoles={['installer', 'admin']}>
                      <InstallerBidsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/installer/contracts" element={
                    <ProtectedRoute allowedRoles={['installer', 'admin']}>
                      <InstallerContractsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/installer/earnings" element={
                    <ProtectedRoute allowedRoles={['installer', 'admin']}>
                      <InstallerEarningsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/installer/schedule/:contractId" element={
                    <ProtectedRoute allowedRoles={['installer', 'admin']}>
                      <InstallerSchedulePage />
                    </ProtectedRoute>
                  } />
                  <Route path="/installer/progress/:contractId" element={
                    <ProtectedRoute allowedRoles={['installer', 'admin']}>
                      <ProjectProgressPage />
                    </ProtectedRoute>
                  } />

                  {/* ========== مسیرهای درخواست نصب ========== */}
                  <Route path="/installations" element={
                    <ProtectedRoute>
                      <InstallationRequestsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/installations/:id" element={
                    <ProtectedRoute>
                      <RequestDetailPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/installer/orders" element={
                    <ProtectedRoute allowedRoles={['installer', 'admin']}>
                      <InstallerOrdersPage />
                    </ProtectedRoute>
                  } />
                  {/* ========== مسیر 404 ========== */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </main>
              <Footer />
              <Toaster position="top-center" reverseOrder={false} />
            </div>
          </SEOProvider>
        </Router>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;